import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import CopyToClipboard from "react-copy-to-clipboard";
import { BsCheck2 } from "react-icons/bs";
import { IoCopy } from "react-icons/io5";
import { FiLock } from "react-icons/fi";
import { Eye, EyeOff, TrendingUp } from "lucide-react";
import { useParams } from "react-router-dom";
import {
  useCheckWatchCoinDepositsQuery,
  useCreateCoinDepositAddressMutation,
  useGetCoinAddressQuery,
  useWatchCoinDepositsMutation,
} from "../../../features/quick-sell/quickSellSlice";
import { useGetCoinRateQuery, useGetCoinTrendQuery } from "../../../features/trade&coins/bitcoinSlice";
import { useGetAllCoins24hChangesQuery } from "../../../features/trade&coins/coinsChangesSlice";
import { Loader } from "../../../shared/Loader";
import { RatePreviewSkeleton } from "./RatePreviewSkeleton";
import { MarketTrendChart } from "./MarketTrendChart";
import toast from "react-hot-toast";
import { coinMeta, isNetworkDisabledError, NETWORK_DISABLED_COPY, sendOnlyCopy } from "../../../features/quick-sell/coinMeta";

export const SellBitcoin = () => {
  const coin = useParams<{ coin: string }>().coin as string;

  // Stablecoin tokens (usdt-erc20, usdc-sol, …) price through usdt/usdc.
  const meta = coinMeta(coin);

  const { data: btcRate } = useGetCoinRateQuery(meta.priceCoin, {
    refetchOnMountOrArgChange: true,
    skip: coin?.toLowerCase() === "usdt",
  });

  // Fetch trend data for the coin
  const { data: trendData, isLoading: isTrendLoading } = useGetCoinTrendQuery(
    meta.priceCoin,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  // Use bulk query and extract specific coin data
  const { data: allCoinsChanges, isLoading: isChangeLoading } = useGetAllCoins24hChangesQuery();
  const coinChange = allCoinsChanges?.[meta.priceCoin];

  const { data, isError, error, isLoading, isSuccess, isFetching } =
    useGetCoinAddressQuery(coin, {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    });
  const [createAddress, { isLoading: isCreateDepositLoading }] =
    useCreateCoinDepositAddressMutation();

  return (
    <div className="space-y-8">
      <Card
        coin={coin}
        paymentAddress={data?.address}
        onGenerateAddress={(coin: string) => {
          createAddress(coin);
        }}
        isLoading={isCreateDepositLoading}
        loadingData={isLoading || isFetching}
        rate={btcRate}
        coinChange={coinChange}
        isChangeLoading={isChangeLoading}
        isError={isError}
        networkDisabled={isNetworkDisabledError(error)}
        trendData={trendData}
        isTrendLoading={isTrendLoading}
      />
    </div>
  );
};

export const Card = ({
  paymentAddress,
  onGenerateAddress,
  isLoading,
  coin,
  loadingData,
  rate,
  coinChange,
  isChangeLoading,
  isError = false,
  networkDisabled = false,
  trendData,
  isTrendLoading,
}: {
  paymentAddress: string;
  onGenerateAddress: CallableFunction;
  isLoading: boolean;
  coin: string;
  loadingData: boolean;
  rate: any;
  coinChange?: any;
  isChangeLoading?: boolean;
  isError?: boolean;
  /** 503 NETWORK_DISABLED from the backend kill switch. */
  networkDisabled?: boolean;
  trendData?: any;
  isTrendLoading?: boolean;
}) => {
  const [copied, setCopied] = useState(false);
  const [currency, setCurrency] = useState<"usd" | "ngn">("usd");
  const [showAddress, setShowAddress] = useState(true);
  const [currentAddress, setCurrentAddress] = useState<string | undefined>(paymentAddress);

  const handleGenerateAddress = () => {
    onGenerateAddress(coin);
  };

  const [watchDeposits] = useWatchCoinDepositsMutation();
  const [reveal, setReveal] = useState(false);
  const { data, isLoading: isWatchLoading } = useCheckWatchCoinDepositsQuery(
    coin,
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    }
  );

  useEffect(() => {
    if (data?.isActive) {
      setReveal(true);
    } else {
      setReveal(false);
    }
  }, [data]);

  useEffect(() => {
    setCopied(false);
    // Reset address when coin changes to prevent showing previous coin's address
    setCurrentAddress(undefined);
  }, [coin]);

  // Update currentAddress when paymentAddress prop changes
  useEffect(() => {
    setCurrentAddress(paymentAddress);
  }, [paymentAddress]);

  useEffect(() => {
    if (!data?.isActive) {
      setReveal(false);
    }
  }, [coin]);

  const handleWatchDeposits = async () => {
    try {
      await watchDeposits(coin).unwrap();
      setReveal(true);
    } catch (e) {
      console.log(e);
    }
  };

  // Format rate values
  const ngnUsdRate = rate?.usd ? (Number(rate?.ngn || 0) / Number(rate?.usd || 1)).toFixed(2) : "0.00";
  const usdCoinRate = rate?.usd || "0";
  const ngnCoinRate = rate?.ngn ? Number(rate.ngn).toLocaleString() : "0";
  const changeValue = coinChange?.usd_24h_change || 0;
  const isPositiveChange = changeValue >= 0;
  const changeText = isPositiveChange ? `+${changeValue.toFixed(2)}%` : `${changeValue.toFixed(2)}%`;
  const changeColor = isPositiveChange ? "text-green-500" : "text-red-500";
  const changeArrow = isPositiveChange ? "↑" : "↓";

  const { symbol, network } = coinMeta(coin);

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="bg-black rounded-2xl p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-4xl font-bold text-white mb-2">Sell {symbol}</h2>
            <p className="text-gray-400">Quick and secure crypto transactions</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 border border-primary/30">
            <TrendingUp className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary">Live</span>
          </div>
        </div>
      </div>
      {loadingData ? (
        <div className="px-8 py-12 min-h-[40vh] flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <>
          {/* Rate Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-black rounded-xl p-6">
              <p className="text-xs font-semibold text-gray-400 mb-3">NGN/USD</p>
              <p className="text-2xl font-bold text-white mb-2">{ngnUsdRate}</p>
              <p className={`text-xs font-semibold ${changeColor}`}>{changeArrow} {changeText}</p>
            </div>
            <div className="bg-black rounded-xl p-6">
              <p className="text-xs font-semibold text-gray-400 mb-3">USD/{symbol}</p>
              <p className="text-2xl font-bold text-white mb-2">{usdCoinRate}</p>
              <p className="text-xs text-green-500 font-semibold">↑ Current</p>
            </div>
            <div className="bg-black rounded-xl p-6">
              <p className="text-xs font-semibold text-gray-400 mb-3">NGN/{symbol}</p>
              <p className="text-2xl font-bold text-white mb-2">{ngnCoinRate}</p>
              <p className="text-xs text-green-500 font-semibold">↑ Rate</p>
            </div>
          </div>

          {/* Chart Section */}
          <div className="bg-black rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">Market Trend</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrency("usd")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${currency === "usd"
                    ? "bg-primary text-black"
                    : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                    }`}
                >
                  USD
                </button>
                <button
                  onClick={() => setCurrency("ngn")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${currency === "ngn"
                    ? "bg-primary text-black"
                    : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                    }`}
                >
                  NGN
                </button>
              </div>
            </div>
            <MarketTrendChart
              data={trendData}
              isLoading={isTrendLoading || false}
              currency={currency}
            />
          </div>

          {/* QR Code & Address Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* QR Code */}
            <div className="bg-black rounded-2xl p-6 flex flex-col items-center relative">
              {network && (
                <div className="w-full flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Network</span>
                  <span className="px-3 py-1 rounded-full bg-primary text-black text-xs font-bold">{network}</span>
                </div>
              )}
              {networkDisabled ? (
                <div className="h-40 rounded-lg flex items-center justify-center mb-4 px-6">
                  <p className="text-center text-gray-200 text-sm">{NETWORK_DISABLED_COPY}</p>
                </div>
              ) : currentAddress && !isError ? (
                <>
                  {!reveal && coin.toLowerCase() !== "usdt" && (
                    <div className="absolute inset-0 bg-black/90 rounded-2xl flex items-center justify-center z-10">
                      <div className="text-center">
                        {!isWatchLoading && (
                          <button
                            className="bg-primary hover:brightness-105 text-black font-bold py-3 px-8 rounded-lg transition-all duration-200 transform hover:scale-105"
                            onClick={handleWatchDeposits}
                          >
                            Proceed to sell
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="w-40 h-40 bg-white rounded-lg flex items-center justify-center mb-4 p-4">
                    <QRCode value={currentAddress} size={160} />
                  </div>
                  <p className="text-xs text-gray-400 mb-3">Scan to pay</p>
                  <CopyToClipboard
                    text={currentAddress}
                    onCopy={() => {
                      setCopied(true);
                      toast.success("Copied to clipboard");
                      setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    <button className="text-sm font-semibold text-primary hover:text-primary/80 transition">
                      {copied ? "Copied!" : "Click to copy"}
                    </button>
                  </CopyToClipboard>
                </>
              ) : (
                <div className=" h-40 rounded-lg flex items-center justify-center mb-4 px-4">
                  <div className="text-center">
                    <p className="text-gray-200 text-sm mb-2">No QR Code</p>
                    <button
                      onClick={handleGenerateAddress}
                      disabled={isLoading}
                      className="bg-primary hover:brightness-105 text-black font-bold py-2 px-6 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {isLoading ? "Generating..." : "Generate Address"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Address Copy */}
            <div className="bg-black rounded-2xl p-6 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Wallet Address
                </label>
                {network && (
                  <span className="px-3 py-1 rounded-full bg-primary text-black text-xs font-bold">{network}</span>
                )}
              </div>
              <div className="flex-1 flex items-center mb-4">
                <input
                  type={showAddress ? "text" : "password"}
                  value={currentAddress || ""}
                  readOnly
                  className="flex-1 bg-gray-800 rounded-lg px-4 py-3 text-sm font-mono text-white outline-none border border-gray-700"
                  placeholder="No address generated"
                />
              </div>
              <div className="flex gap-2">
                <CopyToClipboard
                  text={currentAddress || ""}
                  onCopy={() => {
                    setCopied(true);
                    toast.success("Copied to clipboard");
                    setTimeout(() => setCopied(false), 2000);
                  }}
                >
                  <button
                    disabled={!currentAddress}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary text-black font-semibold hover:opacity-90 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <IoCopy className="w-4 h-4" />
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </CopyToClipboard>
                {currentAddress && (
                  <button
                    onClick={() => setShowAddress(!showAddress)}
                    className="px-4 py-3 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition border border-gray-700"
                  >
                    {showAddress ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="bg-primary/10 border border-primary/30 rounded-xl p-6">
            <div className="flex gap-4">
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-primary">✓</span>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Secure & Verified Transaction</h4>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• {sendOnlyCopy(symbol, network)}</li>
                  <li>• Your deposit is credited automatically once the network confirms it.</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Card;
